/**
 * Errores de dominio.
 *
 * Cada error lleva la información necesaria para construir una respuesta
 * RFC 9457 (Problem Details) en la capa HTTP, incluido `retryable`, que
 * forma parte del contrato con el cliente offline: el dispositivo lo usa
 * para decidir si reintenta el evento o lo marca como ERROR
 * (API-DESIGN.md §6, SYNC-ENGINE.md §2.2).
 */

export type ErrorCode =
  | 'VALIDATION_FAILED'
  | 'NOT_FOUND'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'CONFLICT'
  | 'VERSION_CONFLICT'
  | 'RULE_VIOLATION'
  | 'RULE_PERFORMER_NOT_ALLOWED'
  | 'IMMUTABLE_RESOURCE'
  | 'RATE_LIMITED'
  | 'TENANT_CONTEXT_MISSING'
  | 'INTERNAL';

export abstract class DomainError extends Error {
  abstract readonly code: ErrorCode;
  abstract readonly httpStatus: number;
  /** ¿Tiene sentido que el cliente reintente este evento? */
  readonly retryable: boolean = false;
  /** Datos adicionales expuestos en la respuesta (sin información sensible). */
  readonly details?: Readonly<Record<string, unknown>>;

  protected constructor(message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = new.target.name;
    this.details = details ? Object.freeze({ ...details }) : undefined;
  }
}

export class ValidationError extends DomainError {
  readonly code = 'VALIDATION_FAILED' as const;
  readonly httpStatus = 400;
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, details);
  }
}

export class NotFoundError extends DomainError {
  readonly code = 'NOT_FOUND' as const;
  readonly httpStatus = 404;
  constructor(resource: string, id?: string) {
    super(`No encontrado: ${resource}${id ? ` (${id})` : ''}`, { resource, id });
  }
}

export class UnauthenticatedError extends DomainError {
  readonly code = 'UNAUTHENTICATED' as const;
  readonly httpStatus = 401;
  constructor(message = 'Autenticación requerida') {
    super(message);
  }
}

/**
 * Prohibido. Nunca revela si el recurso existe: en un sistema multiempresa,
 * distinguir 403 de 404 filtra la existencia de datos de otro tenant.
 */
export class ForbiddenError extends DomainError {
  readonly code = 'FORBIDDEN' as const;
  readonly httpStatus = 403;
  constructor(message = 'No tiene permisos para realizar esta acción', details?: Record<string, unknown>) {
    super(message, details);
  }
}

export class ConflictError extends DomainError {
  readonly code = 'CONFLICT' as const;
  readonly httpStatus = 409;
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, details);
  }
}

/** Bloqueo optimista: la entidad cambió desde que el cliente la leyó. */
export class VersionConflictError extends DomainError {
  readonly code = 'VERSION_CONFLICT' as const;
  readonly httpStatus = 409;
  constructor(resource: string, expected: number | bigint, actual: number | bigint) {
    super(`Conflicto de versión en ${resource}: se esperaba ${expected}, hay ${actual}`, {
      resource,
      expected: String(expected),
      actual: String(actual),
    });
  }
}

/**
 * Violación de una regla del motor normativo.
 * Incluye la referencia al artículo para que el usuario sepa POR QUÉ.
 */
export class RuleViolationError extends DomainError {
  readonly code: ErrorCode;
  readonly httpStatus = 422;
  constructor(
    message: string,
    readonly regulationRef?: string,
    code: ErrorCode = 'RULE_VIOLATION',
    details?: Record<string, unknown>,
  ) {
    super(message, { ...details, regulationRef });
    this.code = code;
  }
}

/**
 * Recurso inmutable: documento emitido, factura, revisión firmada.
 * Corregir significa crear una versión nueva (§119 del prompt maestro).
 */
export class ImmutableResourceError extends DomainError {
  readonly code = 'IMMUTABLE_RESOURCE' as const;
  readonly httpStatus = 423;
  constructor(resource: string, id?: string) {
    super(
      `${resource} es inmutable y no puede modificarse. Emita una corrección o una versión nueva.`,
      { resource, id },
    );
  }
}

export class RateLimitedError extends DomainError {
  readonly code = 'RATE_LIMITED' as const;
  readonly httpStatus = 429;
  override readonly retryable = true;
  constructor(readonly retryAfterSeconds: number) {
    super('Demasiadas peticiones', { retryAfterSeconds });
  }
}

/**
 * Se ha intentado acceder a datos sin contexto de empresa.
 * Es un fallo de programación, no del usuario: nunca debe llegar a producción.
 */
export class TenantContextMissingError extends DomainError {
  readonly code = 'TENANT_CONTEXT_MISSING' as const;
  readonly httpStatus = 500;
  constructor() {
    super('No hay contexto de empresa en la petición (company_id)');
  }
}

export function isDomainError(error: unknown): error is DomainError {
  return error instanceof DomainError;
}
